import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (message: string, variant?: ToastVariant, duration?: number) => void;
  removeToast: (id: string) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message: string, variant: ToastVariant = 'info', duration = 5000) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setToasts((prev) => [...prev, { id, message, variant, duration }]);
    if (duration > 0) setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  const success = useCallback((msg: string, d?: number) => addToast(msg, 'success', d), [addToast]);
  const error = useCallback((msg: string, d?: number) => addToast(msg, 'error', d), [addToast]);
  const info = useCallback((msg: string, d?: number) => addToast(msg, 'info', d), [addToast]);
  const warning = useCallback((msg: string, d?: number) => addToast(msg, 'warning', d), [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, info, warning }}>
      {children}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
          {toasts.map((toast) => {
            const styles: Record<ToastVariant, { bg: string; text: string }> = {
              success: { bg: 'bg-accent/10 border-accent/20', text: 'text-emerald-100' },
              error: { bg: 'bg-red-500/10 border-red-500/20', text: 'text-red-100' },
              info: { bg: 'bg-sky-500/10 border-sky-500/20', text: 'text-sky-100' },
              warning: { bg: 'bg-yellow-500/10 border-yellow-500/20', text: 'text-yellow-100' },
            };
            const s = styles[toast.variant];
            return (
              <div key={toast.id} className={`flex items-start gap-3 px-4 py-3 rounded-2xl border backdrop-blur-md ${s.bg} ${s.text} shadow-card-lg animate-slide-up`}>
                <p className="flex-1 text-sm">{toast.message}</p>
                <button onClick={() => removeToast(toast.id)} className="flex-shrink-0 p-1 hover:bg-white/10 rounded transition-colors" aria-label="Dismiss">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
